// Consumi (S7; tela, artboard 37): il totale per giorno, in token e in euro quando il provider
// dà un costo, e la ripartizione per progetto. Il periodo si sceglie qui.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { Segmented } from '../../components/Segmented';
import { KpiTile } from '../control/KpiTile';
import { SkeletonRow } from '../../components/Skeleton';
import { api } from '../../lib/api';
import type { Project, Usage as UsageData } from '../../lib/api';
import { eur, tokens as fmt } from '../../lib/format';
import { Bars } from '../control/widgets/Bars';

const ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const sum = (v: number[]) => v.reduce((a, b) => a + b, 0);

export function Usage() {
  const [days, setDays] = useState<'7' | '30' | '90'>('30');
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  useEffect(() => {
    setUsage(null);
    void api.usage(Number(days), ZONE).then(setUsage, () => setUsage(null));
  }, [days]);
  useEffect(() => {
    void api.projects().then(
      (r) => setProjects(r.projects),
      () => setProjects([]),
    );
  }, []);
  const rows = usage
    ? Object.entries(usage.byProject)
        .map(([id, v]) => ({
          id,
          name: projects.find((p) => p.id === id)?.name ?? id,
          tokens: sum(v.tokens),
          cost: sum(v.costEur),
        }))
        .sort((a, b) => b.tokens - a.tokens)
    : [];
  const other = usage ? sum(usage.tokens) - sum(rows.map((r) => r.tokens)) : 0;
  return (
    <div className="page" style={{ display: 'grid', gap: 16 }}>
      <PageHeader
        title="Consumi"
        subtitle="Il totale di PopTown: token e spesa per giorno, e quanto ha usato ogni progetto."
        actions={
          <Segmented
            label="Periodo"
            value={days}
            items={[
              { value: '7', label: '7 giorni' },
              { value: '30', label: '30 giorni' },
              { value: '90', label: '90 giorni' },
            ]}
            onChange={setDays}
          />
        }
      />
      {!usage ? (
        <SkeletonRow />
      ) : (
        <>
          <div
            className="dash-kpis"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              marginBottom: 16,
            }}
          >
            <KpiTile label="Token" value={fmt(sum(usage.tokens))} pop="analyst-01" />
            <KpiTile
              label="Spesa"
              value={eur(sum(usage.costEur))}
              note="dove il provider la dà"
              pop="data-scientist-01"
            />
            <KpiTile label="Progetti" value={rows.length} pop="team-lead-01" />
          </div>
          <section className="dash-widget" data-tint="sun" style={{ marginBottom: 16 }}>
            <h2 className="dash-widget-title">Token al giorno</h2>
            <Bars
              values={usage.tokens}
              days={usage.days}
              height={200}
              rainbow={usage.tokens.length <= 31}
              format={(v) => `${fmt(v)} token`}
            />
          </section>
          <section className="dash-widget" data-tint="mint">
            <h2 className="dash-widget-title">Per progetto</h2>
            {rows.map((r) => (
              <div key={r.id} className="np-row">
                <Link
                  to={`/progetti/${r.id}`}
                  className="t-strong"
                  style={{ flex: 1, color: 'inherit' }}
                >
                  {r.name}
                </Link>
                <span className="t-strong">{fmt(r.tokens)}</span>
                <span className="t-caption dim" style={{ width: 90, textAlign: 'right' }}>
                  {r.cost ? eur(r.cost) : '—'}
                </span>
              </div>
            ))}
            <div className="np-row">
              <span className="t-body dim" style={{ flex: 1 }}>
                Senza progetto e voce di sistema
              </span>
              <span className="t-strong">{fmt(Math.max(0, other))}</span>
              <span style={{ width: 90 }} />
            </div>
          </section>
        </>
      )}
    </div>
  );
}
